import {Banner} from '@wandb/components';
import {useLayoutEffect, useRef} from 'react';

import {
  setViewerUsingAdminPrivileges,
  stopImpersonating,
  viewingAs,
} from '../admin/adminState';
import type {AdminTools} from '../admin/useAdminTools';
import css from './AdminStateBanner.module.css';

/* The ARIA drawer is portaled to document.body, so the banner's height is
 * published on :root for --wb-agent-drawer-top rather than inherited. */
export const SHELL_BANNER_HEIGHT_VAR = '--shell-banner-height';

const useMeasuredHeight = () => {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const element = ref.current;
    const root = document.documentElement;
    if (element == null) {
      return;
    }
    const publish = () =>
      root.style.setProperty(
        SHELL_BANNER_HEIGHT_VAR,
        `${element.getBoundingClientRect().height}px`
      );
    publish();
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(publish);
    observer?.observe(element);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(SHELL_BANNER_HEIGHT_VAR);
    };
  }, []);
  return ref;
};

export const AdminStateBanner = ({adminTools}: {adminTools: AdminTools}) => {
  const ref = useMeasuredHeight();
  const {isAdminModeActive, isImpersonating} = adminTools;
  const impersonated = viewingAs();

  return (
    <div ref={ref} className={css.wrapper}>
      <Banner
        variant="warning"
        isVisible
        isDismissible={false}
        aria-label="Elevated access">
        <Banner.Body>
          <span className={css.body}>
            {isAdminModeActive && <strong>Admin mode</strong>}
            {isAdminModeActive && isImpersonating && (
              <span aria-hidden="true">·</span>
            )}
            {isImpersonating && (
              <span>
                Impersonating <strong>{impersonated}</strong>
              </span>
            )}
          </span>
        </Banner.Body>
        {isImpersonating ? (
          <Banner.Action
            onPress={() => {
              stopImpersonating();
              window.location.reload();
            }}>
            Stop
          </Banner.Action>
        ) : (
          <Banner.Action
            onPress={() => {
              setViewerUsingAdminPrivileges(false);
              window.location.reload();
            }}>
            Turn off
          </Banner.Action>
        )}
      </Banner>
    </div>
  );
};
